/**
 * useKeyboardShortcuts - Phase 7
 * 
 * Provides keyboard shortcuts for power users in FinTasks.
 * 
 * Shortcuts:
 * - N: New task
 * - /: Focus search
 * - ?: Show shortcuts help
 * - Escape: Close modals / Exit modes
 * - 1-5: Quick filter (All, Today, Overdue, Completed, Archived)
 * - R: Toggle reorder mode
 * - S: Toggle select mode
 * - A: Select all (when in select mode)
 * - D: Delete selected (when in select mode)
 * - C: Complete selected (when in select mode)
 * 
 * @module useKeyboardShortcuts
 */

import { useEffect, useCallback, useState } from 'react';

// ============================================================================
// SHORTCUT DEFINITIONS
// ============================================================================

export const KEYBOARD_SHORTCUTS = {
  // Navigation & Actions
  NEW_TASK: { key: 'n', description: 'New task', category: 'Actions' },
  SEARCH: { key: '/', description: 'Focus search', category: 'Navigation' },
  HELP: { key: '?', description: 'Show shortcuts', category: 'Help' },
  ESCAPE: { key: 'Escape', description: 'Close / Cancel', category: 'Navigation' },
  
  // Filters (1-5)
  FILTER_ALL: { key: '1', description: 'All tasks', category: 'Filters' },
  FILTER_TODAY: { key: '2', description: 'Today\'s tasks', category: 'Filters' },
  FILTER_OVERDUE: { key: '3', description: 'Overdue tasks', category: 'Filters' },
  FILTER_COMPLETED: { key: '4', description: 'Completed tasks', category: 'Filters' },
  FILTER_ARCHIVED: { key: '5', description: 'Archived tasks', category: 'Filters' },
  
  // Modes
  REORDER_MODE: { key: 'r', description: 'Toggle reorder mode', category: 'Modes' },
  SELECT_MODE: { key: 's', description: 'Toggle select mode', category: 'Modes' },
  
  // Bulk Actions (when in select mode)
  SELECT_ALL: { key: 'a', description: 'Select all', category: 'Bulk Actions', requiresSelectMode: true },
  DELETE_SELECTED: { key: 'd', description: 'Delete selected', category: 'Bulk Actions', requiresSelectMode: true },
  COMPLETE_SELECTED: { key: 'c', description: 'Complete selected', category: 'Bulk Actions', requiresSelectMode: true },
  ARCHIVE_SELECTED: { key: 'x', description: 'Archive selected', category: 'Bulk Actions', requiresSelectMode: true },
};

// ============================================================================
// HOOK
// ============================================================================

export default function useKeyboardShortcuts({
  // Callbacks
  onNewTask,
  onFocusSearch,
  onShowHelp,
  onEscape,
  onFilterChange,
  onToggleReorderMode,
  onToggleSelectMode,
  onSelectAll,
  onDeleteSelected,
  onCompleteSelected,
  onArchiveSelected,
  
  // State
  isSelectMode = false,
  isModalOpen = false,
  enabled = true,
} = {}) {
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);

  const handleKeyDown = useCallback((event) => {
    // Disabled check
    if (!enabled) return;
    
    // Ignore when typing in inputs/textareas (except for Escape)
    const target = event.target;
    const isInput = target.tagName === 'INPUT' || 
                    target.tagName === 'TEXTAREA' || 
                    target.isContentEditable;
    
    if (isInput && event.key !== 'Escape') return;
    
    // Ignore when modals are open (except for Escape)
    if (isModalOpen && event.key !== 'Escape') return;
    
    // Ignore when modifier keys are pressed (except for Shift for ?)
    if (event.ctrlKey || event.altKey || event.metaKey) return;
    
    const key = event.key.toLowerCase();
    
    // Handle each shortcut
    switch (key) {
      case 'n':
        event.preventDefault();
        onNewTask?.();
        break;
        
      case '/':
        event.preventDefault();
        onFocusSearch?.();
        break;
        
      case '?':
        event.preventDefault();
        setShowShortcutsModal(true);
        onShowHelp?.();
        break;
        
      case 'escape':
        event.preventDefault();
        if (showShortcutsModal) {
          setShowShortcutsModal(false);
        } else {
          onEscape?.();
        }
        break;
        
      // Filters (1-5)
      case '1':
        event.preventDefault();
        onFilterChange?.('all');
        break;
      case '2':
        event.preventDefault();
        onFilterChange?.('today');
        break;
      case '3':
        event.preventDefault();
        onFilterChange?.('overdue');
        break;
      case '4':
        event.preventDefault();
        onFilterChange?.('completed');
        break;
      case '5':
        event.preventDefault();
        onFilterChange?.('archived');
        break;
        
      // Modes
      case 'r':
        event.preventDefault();
        onToggleReorderMode?.();
        break;
      case 's':
        event.preventDefault();
        onToggleSelectMode?.();
        break;
        
      // Bulk actions (only in select mode)
      case 'a':
        if (isSelectMode) {
          event.preventDefault();
          onSelectAll?.();
        }
        break;
      case 'd':
        if (isSelectMode) {
          event.preventDefault();
          onDeleteSelected?.();
        }
        break;
      case 'c':
        if (isSelectMode) {
          event.preventDefault();
          onCompleteSelected?.();
        }
        break;
      case 'x':
        if (isSelectMode) {
          event.preventDefault();
          onArchiveSelected?.();
        }
        break;
        
      default:
        break;
    }
  }, [
    enabled,
    isSelectMode,
    isModalOpen,
    showShortcutsModal,
    onNewTask,
    onFocusSearch,
    onShowHelp,
    onEscape,
    onFilterChange,
    onToggleReorderMode,
    onToggleSelectMode,
    onSelectAll,
    onDeleteSelected,
    onCompleteSelected,
    onArchiveSelected,
  ]);

  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [enabled, handleKeyDown]);

  return {
    showShortcutsModal,
    setShowShortcutsModal,
    shortcuts: KEYBOARD_SHORTCUTS,
  };
}
